package ai.jessie.web.work

import ai.jessie.shared.work.WorkResponse
import ai.jessie.shared.work.WorkSelectionsResponse
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.CalendarScrollStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.GroupTableRowVariant
import ai.jessie.web.theme.HeadTableRowVariant
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.TableNumStyle
import ai.jessie.web.theme.TableRowKind
import ai.jessie.web.theme.TableRowStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.TableStyle
import ai.jessie.web.theme.TotalTableRowVariant
import ai.jessie.web.theme.WorkTableRowStyle
import ai.jessie.web.theme.WorkTableStyle
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// Step 2: only the work entries the server returned for this member and organisation.
@Composable
internal fun workList(organisationId: String, state: WorkUiState, actions: WorkActions) {
    val entries = state.entries
    if (entries != null) {
        if (entries.isEmpty()) P(attrs = HintStyle.toAttrs()) { Text("Noch keine Arbeit erfasst.") }
        else workTable(organisationId, entries, state.selections)
    }
    Div(attrs = RowStyle.toAttrs()) {
        themedButton("Arbeitsliste erneut laden", actions.refresh, disabled = state.busy)
    }
}

// Day groups, newest first, each with its total; hours are the returned minutes as decimals.
@Composable
private fun workTable(
    organisationId: String,
    entries: List<WorkResponse>,
    selections: WorkSelectionsResponse?,
) {
    Div(attrs = CardStyle.toAttrs()) {
        Div(attrs = CalendarScrollStyle.toAttrs()) {
            Div(
                attrs =
                    TableStyle.toModifier().then(WorkTableStyle.toModifier()).toAttrs {
                        attr("role", "table")
                        attr("aria-label", "Arbeitseinträge")
                    }
            ) {
                workRow(HeadTableRowVariant) {
                    listOf("Projekt", "Position", "Beschreibung", "Stunden", "Aktion").forEach {
                        Span(attrs = { attr("role", "columnheader") }) { Text(it) }
                    }
                }
                entries
                    .groupBy { it.workDate }
                    .entries
                    .sortedByDescending { it.key }
                    .forEach { (date, day) ->
                        workSummaryRow(
                            GroupTableRowVariant,
                            workDayLabel(date),
                            day.sumOf { it.durationMinutes },
                        )
                        day.forEach { entry -> workEntryRow(organisationId, entry, selections) }
                    }
                workSummaryRow(TotalTableRowVariant, "Total", entries.sumOf { it.durationMinutes })
            }
        }
    }
}

@Composable
private fun workEntryRow(
    organisationId: String,
    entry: WorkResponse,
    selections: WorkSelectionsResponse?,
) {
    val references = workReferences(entry, selections)
    workRow(null) {
        Span(attrs = TableStrongStyle.toAttrs { attr("role", "cell") }) {
            Text(references.project)
        }
        Span(attrs = { attr("role", "cell") }) { Text(references.position) }
        Span(attrs = { attr("role", "cell") }) { Text(entry.description.orEmpty()) }
        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) {
            Text(calendarHours(entry.durationMinutes))
        }
        Div(attrs = { attr("role", "cell") }) {
            linkButton(
                "Bearbeiten",
                "/organisations/$organisationId/work/${entry.id}",
                size = ButtonSize.SMALL,
            )
        }
    }
}

@Composable
private fun workSummaryRow(variant: CssStyleVariant<TableRowKind>, label: String, minutes: Int) {
    workRow(variant) {
        Span(attrs = { attr("role", "cell") }) { Text(label) }
        Span(attrs = { attr("role", "cell") })
        Span(attrs = { attr("role", "cell") })
        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) {
            Text(calendarHours(minutes))
        }
        Span(attrs = { attr("role", "cell") })
    }
}

@Composable
private fun workRow(variant: CssStyleVariant<TableRowKind>?, cells: @Composable () -> Unit) {
    Div(
        attrs =
            TableRowStyle.toModifier(variant).then(WorkTableRowStyle.toModifier()).toAttrs {
                attr("role", "row")
            }
    ) {
        cells()
    }
}

internal fun workDayLabel(date: String): String =
    if (validCalendarDate(date)) "${calendarWeekday(date)}, ${calendarDateLabel(date)}" else date
